/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { UnstyledButton } from '@/shared/ui';
import {
  useClickOutsideClose,
} from '@/shared/ui/components/form/inputs/Select.helpers';
import {
  useDropdownPosition, dropdownPanelStyle,
} from '@/shared/ui/components/form/inputs/use-dropdown-position';
import type { ColumnChoice, ImportRowStatus, PreviewCell } from '../types';
import { localizedChoices } from '../hooks/name-answers';

/** The dot in the row gutter, one colour per status. */
export const STATUS_DOT: Record<ImportRowStatus, string> = {
  new: 'var(--status-success)',
  existing: 'var(--status-warning)',
  repeated: 'var(--status-info)',
  invalid: 'var(--status-error)',
};

export const headerCellStyle: React.CSSProperties = {
  position: 'sticky',
  top: 0,
  zIndex: 1,
  textAlign: 'left',
  padding: '10px 12px',
  background: 'var(--surface-primary)',
  borderBottom: '1px solid var(--border-subtle)',
  borderRight: '1px solid var(--border-ultra-subtle)',
  whiteSpace: 'nowrap',
  fontWeight: 'var(--font-regular)' as unknown as number,
  verticalAlign: 'top',
};

export const rowNumberCellStyle: React.CSSProperties = {
  background: 'var(--surface-primary)',
  borderBottom: '1px solid var(--border-ultra-subtle)',
  borderRight: '1px solid var(--border-subtle)',
  padding: '0 10px',
  whiteSpace: 'nowrap',
  width: 48,
};

/**
 * The colour a cell note is written in.
 *
 * A note on an invalid row is a problem to fix; the same note on a row that
 * matched an existing record is information, not an error, so it must not
 * read as one.
 *
 * @param status - The row's status.
 * @returns The token to colour the note with.
 */
function noteColor(status: ImportRowStatus): string {
  if (status === 'invalid') return 'var(--status-error-text)';
  if (status === 'existing') return 'var(--status-warning-text)';
  if (status === 'repeated') return 'var(--status-info-text)';
  return 'var(--text-secondary)';
}

export interface PreviewCellContentProps {
  cell: PreviewCell;
  status: ImportRowStatus;
  /** True when the column maps to nothing, which greys the whole column out. */
  ignored: boolean;
  /** Replaces this cell's text for the import; `null` restores the sheet's. */
  onEdit?: (text: string | null) => void;
  /** Answers "é pessoa jurídica?" when the cell's issue asks for it. */
  onPersonTypeAnswer?: (answer: 'individual' | 'company') => void;
  /**
   * The values this column accepts, when they are a closed list. Present
   * means the cell is picked, not typed: for a broker, an interest or a
   * property category there is nothing a person can type that is not on
   * the list, and typing it is only a chance to get it wrong.
   */
  options?: ColumnChoice[];
  /** True while a commit is in flight: the cell can't be touched mid-commit. */
  disabled?: boolean;
}

const noteStyle = (color: string): React.CSSProperties => ({
  fontSize: 11,
  fontWeight: 'var(--font-semibold)' as unknown as number,
  marginTop: 2,
  color,
});

/** The borderless control an open cell edit renders, same metrics as the text. */
const editInputStyle: React.CSSProperties = {
  font: 'inherit',
  color: 'var(--text-primary)',
  background: 'var(--surface-raised)',
  border: '1px solid var(--border-strong)',
  borderRadius: 'var(--radius-sm)',
  padding: '0 4px',
  margin: '-1px -5px',
  minWidth: 120,
  outline: 'none',
};

/**
 * One cell of the preview: the value as typed (or as repaired), the note the
 * backend attached to it — and, on a mapped column, a click-to-edit input so
 * the person fixes the cell here instead of re-uploading the file.
 *
 * @param props - The cell, its row status, whether its column is ignored,
 *   and the edit writer (absent on ignored columns).
 * @returns The rendered cell.
 */
export const PreviewCellContent: React.FC<PreviewCellContentProps> = ({
  cell, status, ignored, onEdit, options, onPersonTypeAnswer, disabled = false,
}) => {
  const { t } = useTranslation('features/spreadsheets');
  const [draft, setDraft] = useState<string | null>(null);
  const isError = status === 'invalid' && Boolean(cell.issue);
  const isPersonTypeQuestion = cell.issue?.kind === 'ambiguous_person_type' && Boolean(onPersonTypeAnswer);
  // A suggestion's repaired text is what imports, so it is what the cell shows.
  const shown = cell.suggestion ? cell.suggestion.value : cell.value;
  const hasValue = Boolean(shown);
  const editable = Boolean(onEdit) && !ignored && !disabled;

  const color = (() => {
    if (ignored || !hasValue) return 'var(--plan-feature-off)';
    return 'var(--text-primary)';
  })();

  const openEditor = () => { if (editable && draft === null) setDraft(shown); };

  const commit = () => {
    if (draft === null) return;
    const text = draft.trim();
    setDraft(null);
    if (text !== shown) onEdit?.(text);
  };

  /** A picked value commits on choice: a select has no "finished typing". */
  const pick = (value: string) => {
    setDraft(null);
    if (value !== shown) onEdit?.(value);
  };

  const pickerOpen = draft !== null && Boolean(options);
  // Anchored to the inline trigger, not the whole `<td>`: a row with a note
  // on another cell (duplicate match, validation message) is taller than
  // this one-line trigger, and anchoring to the cell's own bottom edge left
  // a visible gap between the trigger and the menu.
  const pickerTriggerRef = useRef<HTMLDivElement>(null);
  const pos = useDropdownPosition(pickerTriggerRef, pickerOpen);
  useClickOutsideClose(pickerOpen, pickerTriggerRef, () => setDraft(null));
  const listboxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (pickerOpen) listboxRef.current?.focus();
  }, [pickerOpen]);
  // The sheet's own text, kept selectable so opening the picker on a cell
  // the list does not contain shows what is actually there instead of
  // silently reading as the first option.
  const choices = options && localizedChoices(options, t);
  const pickerOptions = choices && !choices.some((option) => option.value === shown)
    ? [{ value: shown, label: hasValue ? shown : t('preview.empty') }, ...choices]
    : choices;

  return (
    <td
      onClick={openEditor}
      style={{
        position: 'relative',
        padding: '9px 12px',
        borderBottom: '1px solid var(--border-ultra-subtle)',
        borderRight: '1px solid var(--border-ultra-subtle)',
        whiteSpace: 'nowrap',
        verticalAlign: 'top',
        background: isError ? 'var(--status-error-subtle)' : 'transparent',
        color,
        cursor: editable && draft === null ? 'text' : undefined,
      }}
    >
      {pickerOpen ? (
        <>
          <div ref={pickerTriggerRef} data-testid="import-cell-picker" aria-label={t('preview.cellEditLabel')} style={editInputStyle}>
            {hasValue ? shown : t('preview.empty')}
          </div>
          {pos && pickerOptions ? createPortal(
            <div
              data-select-portal
              role="listbox"
              aria-label={t('preview.cellEditLabel')}
              tabIndex={-1}
              ref={listboxRef}
              onMouseDown={(e) => e.preventDefault()}
              onKeyDown={(event) => { if (event.key === 'Escape') setDraft(null); }}
              className="border border-(--border-subtle) bg-(--surface-secondary) shadow-[var(--shadow-md)] rounded-(--radius-md) overflow-hidden"
              style={dropdownPanelStyle(pos, 10050)}
            >
              {/* flex-col + items-stretch, not w-full on the buttons -- see
                  FieldChipSelect's matching comment: a `width: 100%` child
                  here blew the whole menu out to its `maxWidth` budget in a
                  real browser instead of hugging the longest option.
                  shrink-0 on every row -- same component's other matching
                  comment: default flex-shrink squeezed every row to fit the
                  240px max-height instead of scrolling past it. */}
              <div className="max-h-60 overflow-auto p-(--space-1) flex flex-col items-stretch">
                {pickerOptions.map((option) => (
                  <UnstyledButton
                    key={option.value}
                    role="option"
                    aria-selected={option.value === shown}
                    onClick={() => pick(option.value)}
                    className="shrink-0 text-left text-sm px-(--space-2) py-(--space-2) rounded-(--radius-sm) hover:bg-[var(--surface-hover)] truncate"
                  >
                    {option.label}
                  </UnstyledButton>
                ))}
              </div>
            </div>,
            document.body,
          ) : null}
        </>
      ) : draft !== null ? (
        // Not the DS TextInput on purpose: this is a cell of the person's own
        // spreadsheet, and the editor has to keep the cell's metrics (same
        // font, same padding) instead of dropping a form field into a table.
        // eslint-disable-next-line no-restricted-syntax
        <input
          data-testid="import-cell-editor"
          autoFocus
          value={draft}
          aria-label={t('preview.cellEditLabel')}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') commit();
            if (event.key === 'Escape') setDraft(null);
          }}
          style={editInputStyle}
        />
      ) : (
        <div
          tabIndex={editable ? 0 : undefined}
          role={editable ? 'button' : undefined}
          onKeyDown={(event) => { if (event.key === 'Enter') openEditor(); }}
        >
          {hasValue ? shown : t('preview.empty')}
        </div>
      )}
      {cell.issue && isPersonTypeQuestion ? (
        <div data-testid="import-cell-issue" style={noteStyle(noteColor(status))}>
          <div>{cell.issue.message}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
            {/* Two plain buttons, not a select: this is a yes/no question
                about the row, not a value to type or pick from a list --
                the cell edit affordances above don't fit it. */}
            {/* eslint-disable-next-line no-restricted-syntax */}
            <button
              type="button"
              data-testid="import-owner-type-individual"
              disabled={disabled}
              onClick={(event) => { event.stopPropagation(); onPersonTypeAnswer?.('individual'); }}
              style={{
                font: 'inherit', fontSize: 11, fontWeight: 'var(--font-semibold)' as unknown as number,
                color: 'var(--text-primary)', background: 'var(--surface-raised)',
                border: '1px solid var(--border-medium)', borderRadius: 'var(--radius-sm)',
                padding: '2px 8px', cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.6 : 1,
              }}
            >
              {t('preview.ownerTypeIndividual')}
            </button>
            {/* eslint-disable-next-line no-restricted-syntax */}
            <button
              type="button"
              data-testid="import-owner-type-company"
              disabled={disabled}
              onClick={(event) => { event.stopPropagation(); onPersonTypeAnswer?.('company'); }}
              style={{
                font: 'inherit', fontSize: 11, fontWeight: 'var(--font-semibold)' as unknown as number,
                color: 'var(--text-primary)', background: 'var(--surface-raised)',
                border: '1px solid var(--border-medium)', borderRadius: 'var(--radius-sm)',
                padding: '2px 8px', cursor: disabled ? 'default' : 'pointer',
                opacity: disabled ? 0.6 : 1,
              }}
            >
              {t('preview.ownerTypeCompany')}
            </button>
          </div>
        </div>
      ) : cell.issue && (
        <div data-testid="import-cell-issue" style={noteStyle(noteColor(status))}>
          {cell.issue.message}
        </div>
      )}
      {!cell.issue && cell.suggestion && (
        <div data-testid="import-cell-suggestion" style={noteStyle('var(--status-info-text)')}>
          {cell.suggestion.message}
        </div>
      )}
      {!cell.issue && !cell.edited && cell.replaced_from !== undefined && (
        <div data-testid="import-cell-replaced-from" style={noteStyle('var(--text-tertiary-soft)')}>
          {t('preview.cellReplacedFrom', { original: cell.replaced_from })}
        </div>
      )}
      {!cell.issue && cell.edited && (
        <div data-testid="import-cell-edited" style={noteStyle('var(--status-info-text)')}>
          {t('preview.cellEdited')}
          {' · '}
          {/* An 11px inline text action inside a cell note; the DS Button's
              smallest size is a block control and would not fit the line. */}
          {/* eslint-disable-next-line no-restricted-syntax */}
          <button
            type="button"
            disabled={disabled}
            onClick={(event) => { event.stopPropagation(); onEdit?.(null); }}
            style={{
              font: 'inherit', color: 'inherit', background: 'none',
              border: 'none', padding: 0, cursor: disabled ? 'default' : 'pointer',
              textDecoration: 'underline',
            }}
          >
            {t('preview.cellRestore')}
          </button>
        </div>
      )}
    </td>
  );
};
